import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Users, MessageSquare, Activity, TrendingUp, ChevronRight, Circle } from 'lucide-react';

const statusColors = { active: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20', inactive: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20', archived: 'text-[#52525b] bg-white/5 border-white/10', provisioning: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/20' };
const planColors = { starter: 'text-[#a1a1aa]', pro: 'text-[#3B82F6]', enterprise: 'text-[#EF694C]' };

export default function SADashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/superadmin/stats')
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = stats ? [
    { label: 'Mandanten gesamt', value: stats.total_tenants, icon: Building2, color: 'text-[#EF694C]', bg: 'bg-[#EF694C]/10' },
    { label: 'Aktive Mandanten', value: stats.active_tenants, icon: Activity, color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
    { label: 'Benutzer gesamt', value: stats.total_users, icon: Users, color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10' },
    { label: 'Offene Konversationen', value: stats.open_conversations, icon: MessageSquare, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
  ] : [];

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-8 h-8 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-[1600px] mx-auto">
      <div className="animate-fade-in-up">
        <h2 className="font-heading font-bold text-2xl text-white">Platform Overview</h2>
        <p className="text-[#a1a1aa] text-sm mt-1">Real-time health and usage across all tenants</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div key={kpi.label} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`bg-[#121214] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors duration-300 animate-fade-in-up stagger-${i + 1}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
              </div>
              <TrendingUp className="w-4 h-4 text-[#52525b]" />
            </div>
            <p className="font-heading font-bold text-3xl text-white">{kpi.value}</p>
            <p className="text-[#a1a1aa] text-sm mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Letzte Mandanten */}
        <div className="bg-[#121214] border border-white/10 rounded-xl animate-fade-in-up stagger-3">
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-[#a1a1aa]">Letzte Mandanten</h3>
            <Link to="/superadmin/tenants" className="text-xs text-[#EF694C] hover:text-[#d65d43] flex items-center gap-1 transition-colors" data-testid="view-all-tenants">
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {stats?.recent_tenants?.map(t => (
              <Link key={t.id} to={`/superadmin/tenants/${t.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-white/[0.03] transition-colors group"
                data-testid={`tenant-row-${t.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#EF694C]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#EF694C]">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white group-hover:text-[#EF694C] transition-colors">{t.name}</p>
                    <p className="text-xs text-[#52525b] font-mono">{t.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${planColors[t.plan]}`}>{t.plan}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-md border font-medium ${statusColors[t.status] || 'text-[#a1a1aa] bg-white/5 border-white/10'}`}>
                    {t.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Platform Health */}
        <div className="bg-[#121214] border border-white/10 rounded-xl animate-fade-in-up stagger-4">
          <div className="p-6 border-b border-white/5">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-[#a1a1aa]">Platform Health</h3>
          </div>
          <div className="p-6 space-y-4">
            {[
              { label: 'API Gateway', status: 'Operational', ok: true },
              { label: 'Database Cluster', status: 'Operational', ok: true },
              { label: 'AI Orchestrator', status: 'Operational', ok: true },
              { label: 'Nachrichten-Warteschlange', status: 'Operational', ok: true },
              { label: 'Inbox Engine', status: 'Not Configured (see docs)', ok: false, warning: true },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-sm text-[#a1a1aa]">{item.label}</span>
                <div className="flex items-center gap-2">
                  <Circle className={`w-2 h-2 fill-current ${item.warning ? 'text-[#F59E0B]' : item.ok ? 'text-[#10B981]' : 'text-[#F43F5E]'}`} />
                  <span className={`text-xs font-medium ${item.warning ? 'text-[#F59E0B]' : item.ok ? 'text-[#10B981]' : 'text-[#F43F5E]'}`}>
                    {item.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="px-6 pb-6">
            <div className="p-3 bg-[#EF694C]/5 border border-[#EF694C]/10 rounded-lg">
              <p className="text-xs text-[#a1a1aa]">
                <span className="text-[#EF694C] font-medium">Inbox Engine</span> is the upstream conversation engine.
                Pro Mandant konfigurieren unter <span className="font-mono text-[#52525b]">Mandanten-Details → Inbox-Engine-Konfiguration</span>.
                Self-hosted at <span className="font-mono text-[#52525b]">https://inbox.yourdomain.com</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
